<template>
  <div class="admin-page">
    <!-- ── 页头 ───────────────────────────────────────────── -->
    <div class="page-head">
      <div class="head-text">
        <h1>动态管理</h1>
        <p>用户动态的内容审核、运营置顶与评论管理</p>
      </div>
    </div>

    <!-- ── 筛选 ───────────────────────────────────────────── -->
    <div class="filter-bar">
      <el-input
        v-model="listQuery.keyword"
        placeholder="搜索动态内容"
        clearable
        prefix-icon="el-icon-search"
        class="search-input"
        @keyup.enter.native="handleFilter"
        @clear="handleFilter"
      />
      <el-select v-model="listQuery.schoolId" placeholder="全部学校" clearable class="school-select" @change="handleFilter">
        <el-option v-for="item in schools" :key="item.id" :label="item.name" :value="item.id" />
      </el-select>
      <el-button type="primary" class="primary-btn" @click="handleFilter">查询</el-button>
      <el-button class="ghost-btn" @click="resetFilter">重置</el-button>
      <div class="filter-tail">共 <b>{{ total }}</b> 条</div>
    </div>

    <!-- ── 列表 ───────────────────────────────────────────── -->
    <div class="table-card">
      <el-table v-loading="listLoading" :data="list" style="width: 100%">
        <el-table-column prop="id" label="ID" width="70" align="center" />
        <el-table-column label="发布者" min-width="170">
          <template slot-scope="{ row }">
            <div class="owner-cell">
              <el-avatar :src="row.authorAvatar" :size="34" class="owner-avatar" />
              <div class="owner-info">
                <div class="owner-name">{{ row.authorNickname }}</div>
                <div class="owner-school">动态 #{{ row.id }}</div>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="内容" min-width="300">
          <template slot-scope="{ row }">
            <div class="moment-content">{{ row.content }}</div>
          </template>
        </el-table-column>
        <el-table-column label="互动" width="170">
          <template slot-scope="{ row }">
            <div class="moment-stats">
              <span><b>{{ row.viewCount || 0 }}</b> 浏览</span>
              <span><b>{{ row.likeCount || 0 }}</b> 点赞</span>
              <span><b>{{ row.commentCount || 0 }}</b> 评论</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="置顶" width="190">
          <template slot-scope="{ row }">
            <!-- 运营置顶（免费，优先级最高） -->
            <template v-if="row.adminPinValid">
              <span class="tag is-rose">
                <i class="el-icon-top" /> 运营 · {{ row.adminPinScope === 'SCHOOL' ? '同校' : '同城' }}
              </span>
              <div class="pin-expire">
                {{ row.adminPinExpireAt ? '至 ' + formatDate(row.adminPinExpireAt) : '永久' }}
              </div>
            </template>
            <!-- 开关还开着但已经过期：提示运营去清理，免得列表里显示成"没置顶"却占着开关 -->
            <div v-else-if="row.isPinned" class="pin-expire is-warn">运营置顶已过期</div>

            <!-- 用户付费置顶：与运营置顶可以并存，所以单独一段展示 -->
            <template v-if="row.pinValid">
              <span class="tag is-coral">
                <i class="el-icon-top" /> 付费 · {{ row.pinScope === 'CITY' ? '同城' : '同校' }}
              </span>
              <div class="pin-expire">至 {{ formatDate(row.pinExpireAt) }}</div>
            </template>

            <span v-if="!row.adminPinValid && !row.pinValid && !row.isPinned" class="cell-sub">—</span>
          </template>
        </el-table-column>
        <el-table-column label="发布时间" width="170">
          <template slot-scope="{ row }">
            <span class="cell-sub">{{ formatDate(row.createdAt) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="240" fixed="right">
          <template slot-scope="{ row }">
            <div class="row-actions">
              <button type="button" class="act act-plain" @click="handleDetail(row)">详情</button>
              <el-dropdown trigger="click" @command="cmd => handleAction(cmd, row)">
                <button type="button" class="act act-plain act-more">
                  操作<i class="el-icon-arrow-down" />
                </button>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item v-if="row.adminPinValid" command="pin">
                    <i class="el-icon-top" /> 修改置顶
                  </el-dropdown-item>
                  <el-dropdown-item v-else command="pin">
                    <i class="el-icon-top" /> 置顶
                  </el-dropdown-item>
                  <el-dropdown-item v-if="row.isPinned" command="unpin">
                    <i class="el-icon-bottom" /> 取消运营置顶
                  </el-dropdown-item>
                  <el-dropdown-item command="delete" divided>
                    <i class="el-icon-delete" /> 删除
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <div class="pager">
        <el-pagination
          v-show="total > 0"
          background
          :current-page="listQuery.page"
          :page-size="listQuery.size"
          :page-sizes="[10, 20, 50]"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </div>

    <!-- ── 运营置顶 ───────────────────────────────────────────
         范围只有「同校 / 同城」两项：同城列表的查询范围是「本城所有学校」，
         本身就包含本校，所以选同城天然在两个列表都出现，不需要再多一个"全场"。 -->
    <el-dialog :title="pinForm.id ? '运营置顶 · 动态 #' + pinForm.id : '运营置顶'" :visible.sync="pinVisible" width="520px">
      <div v-if="pinForm.summary" class="pin-quote">{{ pinForm.summary }}</div>

      <div class="pin-field">
        <div class="pin-field__label">置顶范围</div>
        <div class="pick-row">
          <div
            v-for="opt in scopeOptions"
            :key="opt.value"
            class="pick"
            :class="{ 'is-on': pinForm.pinScope === opt.value }"
            @click="pinForm.pinScope = opt.value"
          >
            <div class="pick__t">{{ opt.label }}</div>
            <div class="pick__d">{{ opt.desc }}</div>
          </div>
        </div>
      </div>

      <div class="pin-field">
        <div class="pin-field__label">
          置顶时长
          <span class="pin-field__hint">到期后自动恢复时间序，无需人工取消</span>
        </div>
        <div class="chip-row">
          <span
            v-for="opt in durationOptions"
            :key="String(opt.value)"
            class="chip"
            :class="{ 'is-on': pinForm.durationHours === opt.value }"
            @click="pinForm.durationHours = opt.value"
          >{{ opt.label }}</span>
        </div>
        <div v-if="pinForm.durationHours !== null" class="custom-dur">
          <el-input-number v-model="pinForm.durationHours" :min="1" :max="8760" :step="1" size="small" controls-position="right" />
          <span class="custom-dur__unit">小时</span>
        </div>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button class="ghost-btn" @click="pinVisible = false">取消</el-button>
        <el-button type="primary" class="primary-btn" :loading="pinSubmitting" @click="submitPin">确定置顶</el-button>
      </div>
    </el-dialog>

    <!-- ── 详情抽屉 ─────────────────────────────────────────
         一屏纵览，不做 tab：运营打开详情要一次性看清
         「用户发了什么 → 有多少人看/赞/评 → 有没有被置顶 → 评论里有什么」。 -->
    <el-drawer
      :visible.sync="detailVisible"
      direction="rtl"
      size="720px"
      :with-header="false"
      custom-class="admin-drawer"
      @closed="onDrawerClosed"
    >
      <div class="drawer-inner">
        <div class="drawer-head">
          <div class="dh-left">
            <span class="dh-id">动态 #{{ momentDetail.id }}</span>
            <span v-if="momentDetail.visibility" class="status-pill is-solid" :class="momentDetail.visibility === 'ALL' ? 's1' : 's0'">
              <i />{{ visibilityText }}
            </span>
            <span v-if="momentDetail.adminPinValid" class="status-pill is-solid s2">
              <i />运营置顶
            </span>
            <span v-if="momentDetail.pinValid" class="status-pill is-solid s0">
              <i />付费置顶
            </span>
          </div>
          <button type="button" class="dh-close" @click="detailVisible = false">
            <i class="el-icon-close" />
          </button>
        </div>

        <div v-loading="detailLoading" class="drawer-body">
          <template v-if="momentDetail.id">
            <!-- ── 动态本体：这一屏的视觉重心 ──────────────────
                 管理端看到的版式必须和用户在心动圈里看到的一致，
                 否则审核结论会建立在"另一个样子"的内容上。 -->
            <div class="md-post">
              <div class="md-post-head">
                <el-avatar :size="44" :src="momentDetail.authorAvatar" class="md-avatar">
                  {{ (momentDetail.authorNickname || '用').slice(0, 1) }}
                </el-avatar>
                <div class="md-post-who">
                  <div class="md-post-name">
                    <span>{{ momentDetail.authorNickname || '用户 ' + momentDetail.userId }}</span>
                    <span v-if="momentDetail.authorIsVerified === 1" class="verify-dot" title="已通过学生认证" />
                    <span
                      v-if="momentDetail.authorGender"
                      class="md-gender"
                      :class="momentDetail.authorGender === 'M' ? 'is-m' : 'is-f'"
                    >{{ momentDetail.authorGender === 'M' ? '男' : '女' }}</span>
                  </div>
                  <div class="md-post-meta">
                    {{ momentDetail.schoolName || '未知学校' }} · ID {{ momentDetail.userId }}
                  </div>
                </div>
              </div>

              <p v-if="momentDetail.content" class="md-post-text">{{ momentDetail.content }}</p>
              <p v-else class="md-post-text is-empty">这条动态没有文字内容</p>

              <div v-if="momentImages.length" class="md-media" :class="'md-media--' + mediaMode">
                <template v-if="mediaMode === 'single'">
                  <div class="md-cell">
                    <el-image :src="momentImages[0]" :preview-src-list="momentImages" fit="contain" />
                  </div>
                </template>
                <template v-else-if="mediaMode === 'three'">
                  <div class="md-cell md-cell--tall">
                    <el-image :src="momentImages[0]" :preview-src-list="momentImages" fit="cover" />
                  </div>
                  <div class="md-cell">
                    <el-image :src="momentImages[1]" :preview-src-list="momentImages" fit="cover" />
                  </div>
                  <div class="md-cell">
                    <el-image :src="momentImages[2]" :preview-src-list="momentImages" fit="cover" />
                  </div>
                </template>
                <template v-else>
                  <div v-for="(img, idx) in momentImages" :key="idx" class="md-cell">
                    <el-image :src="img" :preview-src-list="momentImages" fit="cover" />
                  </div>
                </template>
              </div>

              <video v-if="momentDetail.videoUrl" class="md-video" :src="momentDetail.videoUrl" controls />

              <!-- 时间戳条：位置与文案对齐用户端动态卡（"时间 · N 次浏览"），
                   运营一眼能对上用户截图里的那一行 -->
              <div class="md-post-stamp">
                <span>{{ formatDate(momentDetail.createdAt) }}</span>
                <span v-if="momentDetail.updatedAt && momentDetail.updatedAt !== momentDetail.createdAt">
                  · 最后编辑于 {{ formatDate(momentDetail.updatedAt) }}
                </span>
                <span class="md-post-stamp-tail">{{ momentDetail.viewCount || 0 }} 次浏览</span>
              </div>
            </div>

            <!-- ── 互动：条动态"有多热"，运营判断要不要置顶就看这三个数 ── -->
            <div class="metric-row">
              <div class="metric">
                <b>{{ momentDetail.viewCount || 0 }}<em>次</em></b>
                <span>浏览</span>
              </div>
              <div class="metric">
                <b>{{ momentDetail.likeCount || 0 }}<em>个</em></b>
                <span>点赞</span>
              </div>
              <div class="metric">
                <b>{{ momentDetail.commentCount || 0 }}<em>条</em></b>
                <span>评论</span>
              </div>
            </div>

            <!-- ── 置顶：两套置顶可能同时存在，必须分开说清 ────── -->
            <div class="section">
              <div class="section-title">置顶状态</div>
              <div v-if="!momentDetail.adminPinValid && !momentDetail.pinValid" class="hint-bar">
                这条动态没有被置顶，在动态流里按发布时间排序。
              </div>
              <div v-else class="md-pins">
                <div v-if="momentDetail.adminPinValid" class="md-pin">
                  <span class="tag is-rose"><i class="el-icon-top" /> 运营置顶</span>
                  <span class="md-pin-scope">{{ momentDetail.adminPinScope === 'SCHOOL' ? '同校范围排前' : '同城范围排前' }}</span>
                  <span class="md-pin-time">
                    {{ momentDetail.adminPinExpireAt ? '到期 ' + formatDate(momentDetail.adminPinExpireAt) : '永久有效' }}
                    <b v-if="adminPinLeftText">（{{ adminPinLeftText }}）</b>
                  </span>
                </div>
                <div v-if="momentDetail.pinValid" class="md-pin">
                  <span class="tag is-coral"><i class="el-icon-top" /> 付费置顶</span>
                  <span class="md-pin-scope">{{ momentDetail.pinScope === 'CITY' ? '同城范围排前' : '同校范围排前' }}</span>
                  <span class="md-pin-time">到期 {{ formatDate(momentDetail.pinExpireAt) }}</span>
                </div>
              </div>
              <div v-if="momentDetail.isPinned && !momentDetail.adminPinValid" class="hint-bar md-gap-top">
                运营置顶的开关还开着，但已超过到期时间、实际不再生效 —— 可以取消置顶把它清干净。
              </div>
              <div class="md-pin-note">
                运营置顶免费、优先级高于付费置顶；取消运营置顶不会影响用户花钱买的置顶。
              </div>
            </div>

            <!-- ── 基础信息 ───────────────────────────────────── -->
            <div class="section">
              <div class="section-title">基础信息</div>
              <div class="info-grid">
                <div class="info-item">
                  <label>动态 ID</label>
                  <span>{{ momentDetail.id }}</span>
                </div>
                <div class="info-item">
                  <label>发布者</label>
                  <span>{{ momentDetail.authorNickname || '用户 ' + momentDetail.userId }}（ID {{ momentDetail.userId }}）</span>
                </div>
                <div class="info-item">
                  <label>所属学校</label>
                  <span :class="{ 'is-muted': !momentDetail.schoolName }">{{ momentDetail.schoolName || '未记录' }}</span>
                </div>
                <div class="info-item">
                  <label>可见范围</label>
                  <span>{{ visibilityText }}</span>
                </div>
                <div class="info-item">
                  <label>发布时间</label>
                  <span>{{ formatDate(momentDetail.createdAt) }}</span>
                </div>
                <div class="info-item">
                  <label>图片 / 视频</label>
                  <span :class="{ 'is-muted': !momentImages.length && !momentDetail.videoUrl }">
                    {{ momentImages.length }} 张图片{{ momentDetail.videoUrl ? ' · 含 1 个视频' : '' }}{{ (!momentImages.length && !momentDetail.videoUrl) ? '纯文字动态' : '' }}
                  </span>
                </div>
              </div>
            </div>

            <!-- ── 评论管理 ───────────────────────────────────── -->
            <div class="section">
              <div class="section-title">
                评论管理
                <span class="count">{{ momentDetail.commentCount || 0 }}</span>
              </div>
              <div v-if="comments.length" class="md-cms">
                <div v-for="c in comments" :key="c.id" class="md-cm">
                  <el-avatar :size="30" :src="c.userAvatar" class="md-cm-av">
                    {{ (c.userNickname || '用').slice(0, 1) }}
                  </el-avatar>
                  <div class="md-cm-main">
                    <div class="md-cm-top">
                      <span class="md-cm-name">{{ c.userNickname || '用户 ' + c.userId }}</span>
                      <span v-if="c.replyToNickname" class="md-cm-reply">回复 @{{ c.replyToNickname }}</span>
                      <span class="md-cm-time">{{ formatDate(c.createdAt) }}</span>
                    </div>
                    <div class="md-cm-text">{{ c.content }}</div>
                    <div v-if="Number(c.likeCount) > 0" class="md-cm-like">
                      <i class="el-icon-star-off" /> {{ c.likeCount }} 个赞
                    </div>
                  </div>
                  <button type="button" class="act act-danger" @click="handleDeleteComment(c)">删除</button>
                </div>
                <div v-if="Number(momentDetail.commentCount || 0) > comments.length" class="md-cms-more">
                  仅显示最近 {{ comments.length }} 条，其余请在评论列表页查看
                </div>
              </div>
              <div v-else-if="commentLoading" class="md-cms-loading">评论加载中…</div>
              <div v-else class="empty-state">
                <i class="el-icon-chat-line-round" />
                <p>这条动态还没有人评论</p>
              </div>
            </div>
          </template>
        </div>

        <!-- 底部：动作件收敛在这里，正文只管阅读 -->
        <div class="drawer-foot">
          <el-button v-if="momentDetail.adminPinValid" size="small" class="ghost-btn" @click="handlePin(momentDetail)">
            修改置顶
          </el-button>
          <el-button v-else size="small" class="ghost-btn" @click="handlePin(momentDetail)">
            运营置顶
          </el-button>
          <el-button
            v-if="momentDetail.isPinned"
            size="small"
            class="ghost-btn"
            @click="handleUnpin(momentDetail)"
          >
            取消运营置顶
          </el-button>
          <div class="foot-spacer" />
          <el-button size="small" class="danger-btn" @click="handleDelete(momentDetail)">删除动态</el-button>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import { getMomentList, getMomentDetail, deleteMoment, getComments, deleteComment, pinMoment, unpinMoment } from '@/api/moment'
import { getSchoolOptions } from '@/api/school'

/** 详情抽屉一次拉多少条评论 —— 与后端上限（200）对齐，多出来的靠 commentCount 提示 */
const COMMENT_LIMIT = 50

export default {
  name: 'Moment',
  data() {
    return {
      list: [],
      total: 0,
      listLoading: false,
      schools: [],
      listQuery: {
        page: 1,
        size: 10,
        keyword: '',
        schoolId: ''
      },
      detailVisible: false,
      detailLoading: false,
      commentLoading: false,
      momentDetail: {},
      comments: [],

      // ── 运营置顶弹窗 ──
      pinVisible: false,
      pinSubmitting: false,
      pinForm: {
        id: null,
        pinScope: 'CITY',
        durationHours: 12,
        summary: ''
      },
      scopeOptions: [
        { value: 'SCHOOL', label: '同校', desc: '只在本校动态流排前' },
        { value: 'CITY', label: '同城', desc: '本校 + 同城其他学校都排前' }
      ],
      // null = 永久（后端把 admin_pin_expire_at 留空）
      durationOptions: [
        { label: '1 小时', value: 1 },
        { label: '6 小时', value: 6 },
        { label: '12 小时', value: 12 },
        { label: '24 小时', value: 24 },
        { label: '3 天', value: 72 },
        { label: '7 天', value: 168 },
        { label: '永久', value: null }
      ]
    }
  },
  computed: {
    momentImages() {
      if (!this.momentDetail.images) return []
      try {
        return JSON.parse(this.momentDetail.images)
      } catch (e) {
        return []
      }
    },
    /** 图片版式：与用户端保持同一套 —— 单图宽幅 / 双图并排 / 三图一大多小 / 四图以上宫格 */
    mediaMode() {
      const n = this.momentImages.length
      if (n === 1) return 'single'
      if (n === 2) return 'two'
      if (n === 3) return 'three'
      return 'grid'
    },
    visibilityText() {
      return this.momentDetail.visibility === 'OPPOSITE_GENDER' ? '仅异性可见' : '所有人可见'
    },
    /** 运营置顶剩余时长：运营判断"要不要续"时先看一眼，省得自己拿计算器减 */
    adminPinLeftText() {
      if (!this.momentDetail.adminPinExpireAt) return ''
      const ms = new Date(String(this.momentDetail.adminPinExpireAt).replace(/-/g, '/')) - Date.now()
      if (ms <= 0) return '已到期'
      const hours = Math.floor(ms / 3600000)
      if (hours < 1) return '不足 1 小时'
      if (hours < 24) return '剩余 ' + hours + ' 小时'
      return '剩余 ' + Math.floor(hours / 24) + ' 天'
    }
  },
  created() {
    this.fetchData()
    this.fetchSchools()
  },
  methods: {
    fetchSchools() {
      getSchoolOptions().then(res => {
        this.schools = res.data || []
      }).catch(() => {
        this.schools = []
      })
    },
    fetchData() {
      this.listLoading = true
      getMomentList(this.listQuery).then(res => {
        this.list = res.data.list || []
        this.total = res.data.total || 0
        this.listLoading = false
      })
    },
    handleFilter() {
      this.listQuery.page = 1
      this.fetchData()
    },
    resetFilter() {
      this.listQuery.keyword = ''
      this.listQuery.schoolId = ''
      this.handleFilter()
    },
    handleAction(cmd, row) {
      if (cmd === 'pin') return this.handlePin(row)
      if (cmd === 'unpin') return this.handleUnpin(row)
      if (cmd === 'delete') return this.handleDelete(row)
    },
    handleSizeChange(val) {
      this.listQuery.size = val
      this.fetchData()
    },
    handleCurrentChange(val) {
      this.listQuery.page = val
      this.fetchData()
    },
    handleDetail(row) {
      const id = row.id
      this.detailVisible = true
      this.detailLoading = true
      this.commentLoading = true
      this.momentDetail = {}
      this.comments = []
      // 详情与评论并行拉：串行会让抽屉先空半屏再补上，看起来像卡住了
      getMomentDetail(id)
        .then(res => {
          this.momentDetail = res.data || {}
        })
        .catch(() => {})
        .finally(() => {
          this.detailLoading = false
        })
      getComments(id, { limit: COMMENT_LIMIT })
        .then(res => {
          this.comments = res.data || []
        })
        .catch(() => {})
        .finally(() => {
          this.commentLoading = false
        })
    },
    onDrawerClosed() {
      this.momentDetail = {}
      this.comments = []
    },
    /** 置顶状态变了就重拉详情：抽屉里的标签不能和列表打架 */
    reloadDetail() {
      if (!this.detailVisible || !this.momentDetail.id) return
      getMomentDetail(this.momentDetail.id).then(res => {
        this.momentDetail = res.data || {}
      }).catch(() => {})
    },
    refreshComments() {
      if (!this.momentDetail.id) return
      this.commentLoading = true
      getComments(this.momentDetail.id, { limit: COMMENT_LIMIT })
        .then(res => {
          this.comments = res.data || []
        })
        .catch(() => {})
        .finally(() => {
          this.commentLoading = false
        })
    },
    handleDelete(row) {
      this.$confirm('删除后这条动态和它下面的评论会一起消失，无法恢复。', '删除动态', {
        type: 'warning',
        confirmButtonText: '确定删除',
        cancelButtonText: '取消',
        confirmButtonClass: 'el-button--danger'
      }).then(() => {
        deleteMoment(row.id).then(() => {
          this.$message.success('删除成功')
          // 从详情里删的：抽屉里已经没有内容了，直接关掉
          if (this.momentDetail.id === row.id) {
            this.detailVisible = false
          }
          this.fetchData()
        })
      }).catch(() => {})
    },
    handlePin(row) {
      // 已置顶的进来时把当前设置捞出来，运营改时长不用重新选一遍
      let durationHours = 12
      if (row.adminPinValid && row.adminPinExpireAt) {
        const left = Math.round((new Date(row.adminPinExpireAt.replace(/-/g, '/')) - Date.now()) / 3600000)
        durationHours = left > 0 ? left : 1
      } else if (row.adminPinValid) {
        durationHours = null // 现在是永久
      }
      this.pinForm = {
        id: row.id,
        pinScope: row.adminPinScope === 'SCHOOL' ? 'SCHOOL' : 'CITY',
        durationHours: durationHours,
        summary: (row.content || '').slice(0, 40) + ((row.content || '').length > 40 ? '…' : '')
      }
      this.pinVisible = true
    },
    submitPin() {
      if (this.pinSubmitting) return
      this.pinSubmitting = true
      const { id, pinScope, durationHours } = this.pinForm
      pinMoment(id, { pinScope, durationHours }).then(res => {
        this.$message.success(res.message || '置顶成功')
        this.pinVisible = false
        this.fetchData()
        this.reloadDetail()
      }).catch(() => {
        this.$message.error('置顶失败')
      }).finally(() => {
        this.pinSubmitting = false
      })
    },
    handleUnpin(row) {
      this.$confirm('取消后该动态会回到正常时间序，用户自己付费买的置顶不受影响。', '取消运营置顶', {
        type: 'warning',
        confirmButtonText: '确定取消',
        cancelButtonText: '再想想'
      }).then(() => {
        unpinMoment(row.id).then(res => {
          this.$message.success(res.message || '已取消运营置顶')
          this.fetchData()
          this.reloadDetail()
        })
      }).catch(() => {})
    },
    handleDeleteComment(comment) {
      this.$confirm('删除后这条评论从用户端消失，无法恢复。', '删除评论', {
        type: 'warning',
        confirmButtonText: '确定删除',
        cancelButtonText: '取消',
        confirmButtonClass: 'el-button--danger'
      }).then(() => {
        deleteComment(this.momentDetail.id, comment.id).then(() => {
          this.$message.success('删除成功')
          this.refreshComments()
          // 列表里也有评论数，顺手一起校准
          this.fetchData()
        })
      }).catch(() => {})
    },
    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleString()
    }
  }
}
</script>

<style lang="scss" scoped>
.moment-content {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.5;
  font-size: 13px;
  color: var(--ink-900);
}

.moment-stats {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  font-size: 12px;
  color: var(--ink-400);
}

.moment-stats span {
  background: var(--ink-100);
  border-radius: 5px;
  padding: 2px 7px;
  white-space: nowrap;
}

.moment-stats b {
  color: var(--ink-900);
  font-weight: 600;
}

.pin-expire {
  font-size: 11px;
  color: var(--ink-350);
  margin-top: 3px;
}

.pin-expire.is-warn {
  color: var(--rose-600);
}

/* ── 运营置顶弹窗 ── */
.pin-quote {
  background: var(--ink-100);
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  color: var(--ink-500);
  margin-bottom: 20px;
  line-height: 1.5;
}

.pin-field {
  margin-bottom: 22px;
}

.pin-field__label {
  font-size: 13px;
  font-weight: 600;
  color: var(--ink-700);
  margin-bottom: 10px;
}

.pin-field__hint {
  font-weight: 400;
  font-size: 12px;
  color: var(--ink-350);
  margin-left: 8px;
}

.pick-row {
  display: flex;
  gap: 10px;
}

.pick {
  flex: 1;
  border: 1px solid var(--ink-200);
  border-radius: 10px;
  padding: 12px 14px;
  cursor: pointer;
  transition: all 0.16s;
}

.pick:hover {
  border-color: var(--ink-300);
}

.pick.is-on {
  border-color: var(--rose-400);
  background: var(--rose-50);
}

.pick__t {
  font-size: 14px;
  font-weight: 600;
  color: var(--ink-900);
}

.pick.is-on .pick__t {
  color: var(--rose-600);
}

.pick__d {
  font-size: 12px;
  color: var(--ink-400);
  margin-top: 4px;
  line-height: 1.4;
}

.chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.chip {
  font-size: 13px;
  padding: 5px 14px;
  border-radius: 999px;
  border: 1px solid var(--ink-200);
  color: var(--ink-500);
  cursor: pointer;
  transition: all 0.16s;
}

.chip:hover {
  border-color: var(--ink-300);
}

.chip.is-on {
  background: var(--ink-900);
  border-color: var(--ink-900);
  color: #fff;
}

.custom-dur {
  display: flex;
  align-items: center;
  margin-top: 12px;
}

.custom-dur__unit {
  font-size: 13px;
  color: var(--ink-400);
  margin-left: 8px;
}

/* ============================================================
   动态详情（仅本页结构，类名统一 md- 前缀，不会外溢到别的页面）
   ============================================================ */

/* 动态本体：这一屏的视觉重心。
   白卡 + 玫瑰顶条 —— 与用户详情页的 hero 卡同源，
   让"管理端详情页的重心"在两处长得一样。 */
.md-post {
  background: #fff;
  border: 1px solid var(--ink-200);
  border-top: 3px solid var(--rose-400);
  border-radius: var(--r-lg);
  padding: 18px 20px 16px;
}

.md-post-head {
  display: flex;
  align-items: center;
  gap: 12px;
}

.md-avatar {
  flex: 0 0 auto;
  background: var(--ink-100);
  color: var(--ink-500);
  font-size: 16px;
}

.md-post-who {
  flex: 1;
  min-width: 0;
}

.md-post-name {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 15px;
  font-weight: 600;
  color: var(--ink-900);
}

.md-post-meta {
  margin-top: 3px;
  font-size: 12px;
  color: var(--ink-400);
}

/* 性别只是辅助信息，做成两个低饱和小片，不与认证金点抢注意力 */
.md-gender {
  font-size: 11px;
  font-weight: 400;
  padding: 1px 6px;
  border-radius: 4px;
}

.md-gender.is-m {
  background: #eef3fb;
  color: #4a6fa5;
}

.md-gender.is-f {
  background: var(--rose-50);
  color: var(--rose-600);
}

.md-post-text {
  margin: 14px 0 0;
  font-size: 14px;
  line-height: 1.85;
  color: var(--ink-900);
  white-space: pre-wrap;
  word-break: break-word;
}

.md-post-text.is-empty {
  font-size: 13px;
  color: var(--ink-350);
}

.md-post-stamp {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 14px;
  padding-top: 10px;
  border-top: 1px dashed var(--ink-200);
  font-size: 12px;
  color: var(--ink-400);
}

.md-post-stamp-tail {
  margin-left: auto;
}

/* ---------- 媒体：版式与用户端一致（单图完整 / 双图并排 / 三图一大多小 / 宫格） ---------- */
.md-media {
  display: grid;
  gap: 6px;
  margin-top: 14px;
}

.md-cell {
  border-radius: var(--r-md);
  overflow: hidden;
  background: var(--ink-100);
}

/* el-image 的宽高要一路透传到内部 img，否则格子撑不满 */
.md-cell ::v-deep .el-image {
  display: block;
  width: 100%;
  height: 100%;
}

.md-cell ::v-deep .el-image__inner {
  width: 100%;
  height: 100%;
}

/* 单图：给足高度但不裁切 —— 审核要看到完整内容，不是好看的一小块 */
.md-media--single .md-cell {
  height: 280px;
  background: var(--paper);
}

.md-media--two {
  grid-template-columns: repeat(2, 1fr);
}

.md-media--two .md-cell {
  height: 170px;
}

.md-media--three {
  grid-template-columns: 2fr 1fr;
  grid-template-rows: 1fr 1fr;
  height: 260px;
}

.md-media--three .md-cell--tall {
  grid-row: 1 / 3;
}

.md-media--grid {
  grid-template-columns: repeat(3, 1fr);
}

.md-media--grid .md-cell {
  height: 132px;
}

.md-video {
  display: block;
  width: 100%;
  max-height: 340px;
  margin-top: 12px;
  border-radius: var(--r-md);
  background: #000;
}

/* ---------- 置顶：两套要分开说清，不能糊成一句"已置顶" ---------- */
.md-pins {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.md-pin {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 14px;
  background: #fff;
  border: 1px solid var(--ink-200);
  border-radius: var(--r-md);
}

.md-pin-scope {
  font-size: 13px;
  color: var(--ink-900);
}

.md-pin-time {
  margin-left: auto;
  font-size: 12px;
  color: var(--ink-400);
}

.md-pin-time b {
  font-weight: 500;
  color: var(--rose-600);
}

.md-pin-note {
  margin-top: 8px;
  font-size: 11.5px;
  line-height: 1.6;
  color: var(--ink-350);
}

.md-gap-top {
  margin-top: 10px;
}

/* ---------- 评论 ---------- */
.md-cms {
  display: flex;
  flex-direction: column;
}

.md-cm {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 13px 0;
  border-bottom: 1px solid var(--ink-100);
}

.md-cm:last-child {
  border-bottom: 0;
}

.md-cm-av {
  flex: 0 0 auto;
  background: var(--ink-100);
  color: var(--ink-500);
  font-size: 12px;
}

.md-cm-main {
  flex: 1;
  min-width: 0;
}

.md-cm-top {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.md-cm-name {
  font-size: 13px;
  font-weight: 500;
  color: var(--ink-900);
}

.md-cm-reply {
  font-size: 12px;
  color: var(--rose-600);
}

.md-cm-time {
  margin-left: auto;
  font-size: 11.5px;
  color: var(--ink-350);
}

.md-cm-text {
  margin-top: 5px;
  font-size: 13px;
  line-height: 1.65;
  color: var(--ink-700);
  word-break: break-word;
}

.md-cm-like {
  margin-top: 5px;
  font-size: 11.5px;
  color: var(--ink-400);
}

/* 每行都挂一个红框按钮太吵 —— 默认退成灰字，指上去才亮出来 */
.md-cm .act-danger {
  flex: 0 0 auto;
  border-color: transparent;
  color: var(--ink-350);
}

.md-cm .act-danger:hover {
  color: #fff;
  background: var(--rose-600);
  border-color: var(--rose-600);
}

.md-cms-more,
.md-cms-loading {
  padding: 14px 0 2px;
  text-align: center;
  font-size: 12px;
  color: var(--ink-350);
}
</style>
